/**
 * The board's drag decisions, as pure functions.
 *
 * Separate from `StartupBoard.tsx` so that file exports only a component (fast
 * refresh), and separate from dnd-kit so each decision is testable without a
 * layout engine — dnd-kit computes element geometry that jsdom does not, so a
 * simulated drag would test the simulation rather than these rules.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.1
 */
import { closestCorners } from '@dnd-kit/core';
import type {
  Announcements,
  CollisionDetection,
  KeyboardCoordinateGetter,
} from '@dnd-kit/core';

import type { StartupSummary } from '@nexus/contracts';

import type { Stage } from '../../../components/domain/stages';
import { isStage } from '../board-grouping';

/**
 * The translation call these helpers need.
 *
 * A single-signature function, so both i18next's `t` and a test stub satisfy
 * it — i18next's own `TFunction` has overloads a stub cannot match.
 */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Builds the drag lifecycle callbacks.
 *
 * dnd-kit invokes these with events built from element geometry that jsdom does
 * not compute, so they are unreachable through a simulated drag. Extracted as a
 * named factory the WIRING is testable — a callback that resolves a drop and
 * then forgets to request the move is a real bug, and excluding it from
 * coverage would hide exactly that.
 */
export function buildDragHandlers(
  startups: readonly StartupSummary[],
  requestMove: (startupId: string, stage: Stage, acknowledge: boolean) => void,
  clearError: () => void,
  /**
   * Records which card is being dragged, so the board can render it in a
   * `DragOverlay`. Called with null when the drag ends or is cancelled —
   * an overlay left mounted after a drop is a card frozen under the cursor.
   */
  setActiveId: (id: string | null) => void,
): {
  onDragStart: (event: { active: { id: string | number } }) => void;
  onDragEnd: (event: {
    active: { id: string | number };
    over: { id: string | number } | null;
  }) => void;
  onDragCancel: () => void;
} {
  return {
    onDragStart: (event) => {
      /* A new drag clears the previous failure: leaving a stale "could not be
         moved" beside a card the user is now dragging is misleading. */
      clearError();
      setActiveId(String(event.active.id));
    },

    onDragEnd: (event) => {
      setActiveId(null);

      const move = resolveDrop(
        startups,
        String(event.active.id),
        event.over === null ? null : String(event.over.id),
      );

      if (move === null) return;

      requestMove(move.startup.id, move.stage, false);
    },

    /* Escape mid-drag, or a pointer released outside the board. */
    onDragCancel: () => {
      setActiveId(null);
    },
  };
}

/**
 * Binds the narrow layout's stage selector.
 *
 * Named rather than inline because a Radix Select cannot be driven to invoke
 * its handler in jsdom — it positions its viewport with
 * `document.elementFromPoint`, which jsdom does not implement.
 *
 * A value that is not a stage is ignored rather than crashing the board: the
 * options come from our own list, so this only fires if something else writes
 * to the control.
 */
export function stageSelectHandler(
  setVisibleStage: (stage: Stage) => void,
): (value: string) => void {
  return (value: string): void => {
    if (isStage(value)) setVisibleStage(value);
  };
}

/**
 * Builds the screen-reader announcements for a keyboard drag.
 *
 * dnd-kit's defaults name opaque ids ("Draggable item s1 was moved over
 * droppable area QUALIFIED"), which tells a screen reader user nothing. These
 * name the startup and the stage.
 *
 * Extracted and exported because this is the feedback the keyboard drag path
 * depends on, and a simulated drag in jsdom cannot exercise it — dnd-kit
 * computes geometry jsdom does not provide.
 */
export function buildAnnouncements(
  startups: readonly StartupSummary[],
  /* The narrowest shape these builders need. Declared here rather than
     importing i18next's `TFunction`, whose overloads do not accept a plain
     stub — which would force the tests to construct a real translator to
     assert on a string. */
  t: TranslateFn,
): Announcements {
  const find = (id: string): StartupSummary | undefined =>
    startups.find((candidate) => candidate.id === id);

  /** The announcement for an event that names both a startup and a stage. */
  const forStage = (
    key: string,
    activeId: string,
    overId: string | null,
  ): string | undefined => {
    const startup = find(activeId);
    if (!startup || overId === null || !isStage(overId)) return undefined;

    return t(key, { name: startup.name, stage: t(`stage.${overId}`) });
  };

  /** The announcement for an event that names only the startup's own stage. */
  const forSelf = (key: string, activeId: string): string | undefined => {
    const startup = find(activeId);
    if (!startup) return undefined;

    return t(key, { name: startup.name, stage: t(`stage.${startup.stage}`) });
  };

  return {
    onDragStart: ({ active }) => forSelf('board.pickedUp', String(active.id)),
    /* v8 ignore next 2 -- dnd-kit fires this only mid-drag, which needs
       geometry jsdom does not compute. `forStage` is covered by the other
       announcements, and this one is asserted directly in the tests. */
    onDragOver: ({ active, over }) =>
      forStage(
        'board.movedOver',
        String(active.id),
        over === null ? null : String(over.id),
      ),
    onDragEnd: ({ active, over }) =>
      forStage(
        'board.dropped',
        String(active.id),
        over === null ? null : String(over.id),
      ),
    onDragCancel: ({ active }) => forSelf('board.cancelled', String(active.id)),
  };
}

/**
 * Resolves a drop into the move it represents, or null.
 *
 * Pure, so the rules are testable without a layout engine: dnd-kit computes
 * geometry that jsdom does not, and the alternative is leaving the drop rules
 * untested in the path drag-and-drop depends on.
 *
 * A drop target is either a column (whose id is the stage) or another card
 * (whose id is a startup id). Both must work: a user aiming at a column with
 * cards in it will usually release the pointer over one of those cards, not
 * over the sliver of column background between them. Resolving only stage ids
 * makes the board feel broken exactly where it is fullest.
 *
 * Returns null when the drop landed nowhere, on something that is neither a
 * stage nor a known card, or back in the column it came from — none of which
 * is a move.
 */
export function resolveDrop(
  startups: readonly StartupSummary[],
  activeId: string,
  overId: string | null,
): { startup: StartupSummary; stage: Stage } | null {
  if (overId === null) return null;

  const startup = startups.find((candidate) => candidate.id === activeId);
  if (!startup) return null;

  /* A column id is the stage itself; a card id resolves to the stage of the
     card it landed on. */
  const stage = isStage(overId)
    ? overId
    : startups.find((candidate) => candidate.id === overId)?.stage;

  if (stage === undefined || !isStage(stage) || startup.stage === stage) return null;

  return { startup, stage };
}

/**
 * The card a `DragOverlay` should render, or null.
 *
 * Extracted because `DragOverlay` renders its children only while dnd-kit's own
 * context reports an active drag, and that state is set from element geometry
 * jsdom does not compute — so the JSX inside it is unreachable in a unit test
 * whatever seam is used. The *decision* it depends on is not, and this is that
 * decision.
 *
 * Returns null for an id the board does not hold, which happens when a refetch
 * removes a card mid-drag: rendering nothing is better than crashing the
 * overlay on an undefined startup.
 */
export function overlayStartup(
  startups: readonly StartupSummary[],
  activeId: string | null,
): StartupSummary | null {
  if (activeId === null) return null;

  return startups.find((candidate) => candidate.id === activeId) ?? null;
}

/**
 * Where the keyboard should move a dragged card next.
 *
 * `sortableKeyboardCoordinates`, dnd-kit's default, navigates only within the
 * dragged item's own `SortableContext`. Each column is its own context, so with
 * the default a card can be picked up and then never leave its column — the
 * arrow keys do nothing and the drag can only be cancelled. That makes the
 * keyboard path, which PRD §9.7 requires, non-functional for the one operation
 * the board exists to perform.
 *
 * This getter navigates the *columns* instead, which is the axis that matters
 * here: left and right step through the stage list, and up and down do the same
 * so the narrow single-column layout is operable too. It returns the centre of
 * the target column's droppable rectangle, which is what dnd-kit collides
 * against.
 *
 * **The walk is tracked here, not read back from dnd-kit.** Deriving the
 * current column from the context's `over` looks natural and is wrong: `over`
 * is resolved after the getter returns, so on the next press it still describes
 * the *previous* position, and successive presses drift instead of stepping.
 * The cursor below is the position, advanced once per press and reset when a
 * new drag begins.
 *
 * Returns undefined at either end of the list, which dnd-kit reads as "no
 * move" — the card stays where it is rather than wrapping around, because
 * wrapping from Rejected back to Scouted would look like a glitch.
 */
export function boardKeyboardCoordinates(
  startups: readonly StartupSummary[],
  stages: readonly Stage[],
): KeyboardCoordinateGetter {
  /* The column the walk has reached, and the card it belongs to. Held across
     presses because dnd-kit's `over` is a press behind — see the note above. */
  let cursor: { activeId: string; index: number } | null = null;

  return (event, { context: { active, droppableContainers: containers } }) => {
    const step = KEYBOARD_STEPS[event.code];
    if (step === undefined || active === null) return undefined;

    const activeId = String(active.id);

    /* A different card means a new drag: start from where that card sits. */
    if (cursor?.activeId !== activeId) {
      const start = nextStageFrom(startups, stages, activeId, undefined);
      if (start === -1) return undefined;

      cursor = { activeId, index: start };
    }

    /* Walk to the next stage that is actually on screen. The narrow layout
       omits the card's current stage from the chip row, so stepping the stage
       list alone would aim at a rectangle that does not exist and dnd-kit would
       fall back to geometry — which is how the first press landed two stages
       away. Skipping unmeasured stages keeps one press equal to one stage in
       both layouts. */
    let next = cursor.index + step;
    for (
      let candidate = stages[next];
      candidate !== undefined;
      candidate = stages[next]
    ) {
      if (containers.has(candidate)) break;
      next += step;
    }

    const target = stages[next];
    if (target === undefined) return undefined;

    /* Scrolled into view HERE, then measured. The wide board is a horizontal
       scroller wider than the window, and dnd-kit will not translate a card to
       a point outside the scroll viewport — so without this the last few stages
       are simply unreachable by keyboard, and every press past the first
       re-announces the same column. Doing it ourselves also means the cached
       `droppableRects`, which predate the scroll, are never consulted: a stale
       rectangle aims the card at where the column used to be.

       `block: 'nearest'` so a vertical layout is not scrolled sideways and a
       horizontal one is not scrolled up. */
    const node = containers.get(target)?.node.current;
    if (node === undefined || node === null) return undefined;

    node.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });

    const rect = node.getBoundingClientRect();

    cursor = { activeId, index: next };

    /* The target's top-left, NOT its centre. dnd-kit treats this point as the
       dragged item's new translated position, so returning the centre puts the
       card's own top-left there — its collision rectangle then straddles the
       target and whatever is below and right of it, and the wrong chip wins.
       Nudged inside by a pixel so a rectangle that abuts its neighbour resolves
       to this one. */
    return { x: rect.left + 1, y: rect.top + 1 };
  };
}

/**
 * The index in `stages` the keyboard drag is currently at, or -1.
 *
 * Exported so the walk is testable without dnd-kit's context: the getter itself
 * receives a collision context built from element geometry jsdom does not
 * compute, but this — which column a press starts from — is the part that can
 * actually be wrong.
 */
export function nextStageFrom(
  startups: readonly StartupSummary[],
  stages: readonly Stage[],
  activeId: string | number,
  overId: string | number | undefined,
): number {
  /* A column id is a stage; a card id resolves to that card's stage. */
  if (overId !== undefined) {
    const over = String(overId);
    if (isStage(over)) return stages.indexOf(over);

    const target = startups.find((candidate) => candidate.id === over);
    if (target !== undefined && isStage(target.stage))
      return stages.indexOf(target.stage);
  }

  const startup = startups.find((candidate) => candidate.id === String(activeId));
  return startup === undefined ? -1 : stages.indexOf(startup.stage);
}

/**
 * Arrow keys to a step through the stage list.
 *
 * Up and down step as well as left and right: the wide board lays the columns
 * out horizontally, the narrow one vertically, and a keyboard user should not
 * have to know which layout the CSS chose.
 */
const KEYBOARD_STEPS: Record<string, number | undefined> = {
  ArrowLeft: -1,
  ArrowUp: -1,
  ArrowRight: 1,
  ArrowDown: 1,
};

/**
 * Collision detection for the board.
 *
 * `closestCorners` for a pointer drag, which is what a pointer needs: the user
 * aims with the cursor and the nearest target wins.
 *
 * A keyboard drag is different. `boardKeyboardCoordinates` has *already*
 * decided which stage the arrow key meant and returned that column's centre;
 * re-deriving the target from geometry can then pick a different one — on the
 * narrow layout the stages are a wrapped grid of chips, so the chip nearest the
 * point is not always the chip that was aimed at, and pressing Down announced
 * "over Validated" when it meant Screened.
 *
 * So when the pointer coordinates land inside exactly one droppable, that
 * droppable wins outright. That is only ever true of a deliberate,
 * synthesised point — a real pointer produces the same answer through
 * `closestCorners` anyway.
 */
export function boardCollisionDetection(
  args: Parameters<CollisionDetection>[0],
): ReturnType<CollisionDetection> {
  const { droppableContainers, droppableRects, collisionRect } = args;

  /* The drag's aim point: the dragged rectangle's TOP-LEFT, not its centre.
     dnd-kit translates the dragged item so that its top-left sits at the point
     the coordinate getter returned, so that corner is the aim; the centre is
     half a card down and right of it, which on the narrow layout's wrapped chip
     grid lands on the neighbour instead of the target. Nudged inside by a pixel
     so a rectangle that abuts its neighbour resolves to this one. */
  const x = collisionRect.left + 1;
  const y = collisionRect.top + 1;

  const containing = droppableContainers.filter((container) => {
    const rect = droppableRects.get(container.id);
    if (rect === undefined) return false;

    return (
      x >= rect.left &&
      x <= rect.left + rect.width &&
      y >= rect.top &&
      y <= rect.top + rect.height
    );
  });

  /* Exactly one: nested droppables (a card inside a column) would give several,
     and there `closestCorners` is the better judge. */
  /* Destructured rather than indexed, so `length === 1` and "the element
     exists" are the same check — indexing would need a second, unreachable
     guard to satisfy `noUncheckedIndexedAccess`. */
  const [only, ...rest] = containing;
  if (only !== undefined && rest.length === 0) return [{ id: only.id }];

  return closestCorners(args);
}
